import { useState, useRef, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { useWallet } from "@/contexts/WalletContext";
import { toast } from "@/hooks/use-toast";
import { useTrading } from "@/hooks/use-trading";
import {
  GripVertical,
  X,
  Minimize2,
  Maximize2,
  ArrowUpDown,
  Loader2,
  Wallet
} from "lucide-react";

interface FloatingTradeWidgetProps {
  coin: {
    id: string;
    name: string;
    ticker: string;
    price: number | null;
    mint_address?: string | null;
  };
  onClose?: () => void;
  defaultPosition?: { x: number; y: number };
}

const quickAmounts = [0.1, 0.5, 1, 5];

export function FloatingTradeWidget({ coin, onClose, defaultPosition }: FloatingTradeWidgetProps) {
  const { connected, balance, connect, publicKey } = useWallet();
  const [tradeType, setTradeType] = useState<"buy" | "sell">("buy");
  const [amount, setAmount] = useState("");
  // const [isProcessing, setIsProcessing] = useState(false); // Processed by hook -> see below
  const { executeTrade, isProcessing } = useTrading(coin.id);
  const [isMinimized, setIsMinimized] = useState(false);
  const [position, setPosition] = useState(defaultPosition || { x: window.innerWidth - 340, y: 100 });
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef<HTMLDivElement>(null);
  const dragOffset = useRef({ x: 0, y: 0 });

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!dragRef.current) return;
    setIsDragging(true);
    dragOffset.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    };
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;
      const newX = Math.max(0, Math.min(window.innerWidth - 320, e.clientX - dragOffset.current.x));
      const newY = Math.max(0, Math.min(window.innerHeight - 400, e.clientY - dragOffset.current.y));
      setPosition({ x: newX, y: newY });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging]);

  const estimatedOutput = amount && coin.price
    ? tradeType === "buy"
      ? (parseFloat(amount) / coin.price).toFixed(0)
      : (parseFloat(amount) * coin.price).toFixed(6)
    : "0";

  const handleTrade = async () => {
    if (!connected) {
      await connect();
      return;
    }

    if (!amount || parseFloat(amount) <= 0) {
      toast({ title: "Invalid amount", variant: "destructive" });
      return;
    }

    try {
      await executeTrade(tradeType, parseFloat(amount));
      setAmount("");
      if (onClose) onClose();
    } catch (error) {
      console.error("Trade execution failed:", error);
    }
  };

  if (isMinimized) {
    return (
      <div
        style={{ left: position.x, top: position.y }}
        className="fixed z-50"
      >
        <Card className="p-2 flex items-center gap-2 bg-card/95 backdrop-blur-sm border-primary/20 shadow-xl cursor-move"
          onMouseDown={handleMouseDown}
          ref={dragRef}
        >
          <GripVertical className="w-4 h-4 text-muted-foreground" />
          <span className="font-mono text-sm">${coin.ticker}</span>
          <Badge variant={tradeType === "buy" ? "default" : "destructive"} className="text-xs">
            {tradeType.toUpperCase()}
          </Badge>
          <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => setIsMinimized(false)}>
            <Maximize2 className="w-3 h-3" />
          </Button>
          {onClose && (
            <Button size="icon" variant="ghost" className="h-6 w-6" onClick={onClose}>
              <X className="w-3 h-3" />
            </Button>
          )}
        </Card>
      </div>
    );
  }

  return (
    <div
      style={{ left: position.x, top: position.y }}
      className="fixed z-50 w-[300px]"
    >
      <Card className="bg-card/95 backdrop-blur-sm border-primary/20 shadow-2xl overflow-hidden">
        {/* Header - Draggable */}
        <div
          ref={dragRef}
          onMouseDown={handleMouseDown}
          className={cn(
            "flex items-center justify-between p-3 border-b border-border cursor-move",
            isDragging && "cursor-grabbing"
          )}
        >
          <div className="flex items-center gap-2">
            <GripVertical className="w-4 h-4 text-muted-foreground" />
            <span className="font-bold">${coin.ticker}</span>
            <span className="text-xs text-muted-foreground font-mono">
              ${coin.price?.toFixed(8)}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => setIsMinimized(true)}>
              <Minimize2 className="w-3 h-3" />
            </Button>
            {onClose && (
              <Button size="icon" variant="ghost" className="h-6 w-6" onClick={onClose}>
                <X className="w-3 h-3" />
              </Button>
            )}
          </div>
        </div>

        <div className="p-3 space-y-3">
          {/* Trade Type Toggle */}
          <div className="grid grid-cols-2 gap-1 p-1 bg-secondary rounded-lg">
            <Button
              size="sm"
              variant={tradeType === "buy" ? "default" : "ghost"}
              onClick={() => setTradeType("buy")}
              className={cn(
                "text-sm font-semibold",
                tradeType === "buy" && "bg-success hover:bg-success/90 text-success-foreground"
              )}
            >
              Buy
            </Button>
            <Button
              size="sm"
              variant={tradeType === "sell" ? "default" : "ghost"}
              onClick={() => setTradeType("sell")}
              className={cn(
                "text-sm font-semibold",
                tradeType === "sell" && "bg-destructive hover:bg-destructive/90"
              )}
            >
              Sell
            </Button>
          </div>

          {/* Amount Input */}
          <div className="space-y-2">
            <div className="relative">
              <Input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                className="pr-16 text-lg font-mono"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                {tradeType === "buy" ? "SOL" : coin.ticker}
              </span>
            </div>

            <div className="flex gap-1">
              {quickAmounts.map((qa) => (
                <Button
                  key={qa}
                  size="sm"
                  variant="outline"
                  className="flex-1 text-xs h-7"
                  onClick={() => setAmount(qa.toString())}
                >
                  {qa}
                </Button>
              ))}
            </div>
          </div>

          {/* Output Preview */}
          <div className="p-2 rounded-lg bg-secondary/50 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ArrowUpDown className="w-4 h-4 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">You receive</span>
            </div>
            <span className="font-mono text-sm">
              {estimatedOutput} {tradeType === "buy" ? coin.ticker : "SOL"}
            </span>
          </div>

          {/* Balance */}
          {connected && (
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>Balance:</span>
              <span className="font-mono">{balance.toFixed(4)} SOL</span>
            </div>
          )}

          {/* Trade Button */}
          <Button
            className={cn(
              "w-full font-semibold",
              tradeType === "buy"
                ? "bg-success hover:bg-success/90 text-success-foreground"
                : "bg-destructive hover:bg-destructive/90"
            )}
            onClick={handleTrade}
            disabled={isProcessing}
          >
            {isProcessing ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : !connected ? (
              <>
                <Wallet className="w-4 h-4 mr-2" />
                Connect Wallet
              </>
            ) : (
              `${tradeType === "buy" ? "Buy" : "Sell"} ${coin.ticker}`
            )}
          </Button>
        </div>
      </Card>
    </div>
  );
}
